import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './fonts.css';
import { supabase } from './supabaseClient';

function App() {
  const [dhikrs, setDhikrs] = useState([]);
  const [error, setError] = useState(null);
  const [animatedCounts, setAnimatedCounts] = useState({});
  const [previousCounts, setPreviousCounts] = useState({});
  const [currentCompletion, setCurrentCompletion] = useState(null);
  const [completedCompletions, setCompletedCompletions] = useState([]);
  const [previousCompletedParts, setPreviousCompletedParts] = useState(new Set());
  const [optimisticCompletion, setOptimisticCompletion] = useState(null); // For optimistic updates
  const [amals, setAmals] = useState([
    { id: 1, name: 'نماز جعفر طیار', count: 0 },
    { id: 2, name: 'نماز شب', count: 0 },
    { id: 3, name: 'نماز استغفار', count: 0 },
    { id: 4, name: 'صفحه قرآن', count: 0 },
    // می‌تونی اینجا اعمال بیشتری اضافه کنی:
    // { id: 4, name: 'نماز وتر', count: 0 },
    // { id: 5, name: 'نماز اول وقت', count: 0 },
    // { id: 6, name: 'روزه', count: 0 },
    // { id: 7, name: 'خواندن قرآن', count: 0 },
    // { id: 8, name: 'صدقه', count: 0 }
  ]);
  const [animatedAmalCounts, setAnimatedAmalCounts] = useState({});
  const previousAmalCountsRef = useRef({});

  const normalizeAmals = (list) => {
    const requiredDefaults = [
      { id: 1, name: 'نماز جعفر طیار', count: 0, order_num: 1 },
      { id: 2, name: 'نماز شب', count: 0, order_num: 2 },
      { id: 3, name: 'نماز استغفار', count: 0, order_num: 3 },
      { id: 4, name: 'صفحه قرآن', count: 0, order_num: 4 }
    ];

    const arr = Array.isArray(list) ? list : [];
    const normalized = arr
      .map((a, idx) => {
        const idNum = parseInt(a?.id);
        const countNum = parseInt(a?.count);
        const orderNum = parseInt(a?.order_num);
        return {
          ...a,
          id: Number.isFinite(idNum) ? idNum : a?.id,
          name: typeof a?.name === 'string' ? a.name : String(a?.name || ''),
          count: Number.isFinite(countNum) ? Math.max(0, countNum) : 0,
          order_num: Number.isFinite(orderNum) ? orderNum : (idx + 1)
        };
      })
      .sort((x, y) => (x.order_num || 0) - (y.order_num || 0));

    const byId = new Map(normalized.map(a => [a.id, a]));
    requiredDefaults.forEach((d) => {
      if (!byId.has(d.id)) {
        byId.set(d.id, d);
      }
    });

    return Array.from(byId.values()).sort((x, y) => (x.order_num || 0) - (y.order_num || 0));
  };

  useEffect(() => {
    fetchDhikrs();
    fetchQuranData();
    fetchAmals();
    // Refresh both dhikrs and Quran data every 3 seconds for real-time updates
    const interval = setInterval(() => {
      fetchDhikrs();
      fetchQuranData();
      fetchAmals();
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Listen for amals updates from admin panel
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'amalsUpdated') {
        fetchAmals();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Fetch amals from database
  const fetchAmals = async () => {
    try {
      const { data, error } = await supabase
        .from('amals')
        .select('*')
        .order('order_num', { ascending: true });

      if (error) {
        console.log('Supabase error, using localStorage:', error);
        // Fallback to localStorage
        const savedAmals = localStorage.getItem('amals');
        if (savedAmals) {
          try {
            const parsed = JSON.parse(savedAmals);
            const normalized = normalizeAmals(parsed);
            setAmals(normalized);
            const nextMap = Object.fromEntries((normalized || []).map(a => [a.id, a.count]));
            previousAmalCountsRef.current = nextMap;
          } catch (e) {
            localStorage.removeItem('amals');
          }
        }
      } else {
        const next = normalizeAmals(data || []);

        if (next.length === 0) {
          const savedAmals = localStorage.getItem('amals');
          if (savedAmals) {
            try {
              const parsed = JSON.parse(savedAmals);
              const normalized = normalizeAmals(parsed);
              if (normalized.length > 0) {
                setAmals(normalized);
                const nextMap = Object.fromEntries((normalized || []).map(a => [a.id, a.count]));
                previousAmalCountsRef.current = nextMap;
                return;
              }
              localStorage.removeItem('amals');
            } catch (e) {
              localStorage.removeItem('amals');
            }
          }

          const defaultAmals = [
            { id: 1, name: 'نماز جعفر طیار', count: 0, order_num: 1 },
            { id: 2, name: 'نماز شب', count: 0, order_num: 2 },
            { id: 3, name: 'نماز استغفار', count: 0, order_num: 3 }
          ];

          const normalizedDefaults = normalizeAmals(defaultAmals);
          setAmals(normalizedDefaults);
          localStorage.setItem('amals', JSON.stringify(normalizedDefaults));
          const nextMap = Object.fromEntries(normalizedDefaults.map(a => [a.id, a.count]));
          previousAmalCountsRef.current = nextMap;
          return;
        }

        // Animate changes from 0 to new value (do not animate initial load)
        const nextMap = Object.fromEntries(next.map(a => [a.id, a.count]));
        const prevMap = previousAmalCountsRef.current || {};
        const hasPrev = Object.keys(prevMap).length > 0;
        if (hasPrev) {
          next.forEach((amal) => {
            const prev = prevMap[amal.id];
            if (typeof prev === 'number' && prev !== amal.count) {
              animateAmalCount(amal.id, amal.count);
            }
          });
        }

        setAmals(next);
        previousAmalCountsRef.current = nextMap;
        // Save to localStorage as backup
        localStorage.setItem('amals', JSON.stringify(next));
      }
    } catch (err) {
      console.error('Fetch amals error:', err);
      // Fallback to localStorage
      const savedAmals = localStorage.getItem('amals');
      if (savedAmals) {
        try {
          const parsed = JSON.parse(savedAmals);
          const normalized = normalizeAmals(parsed);
          setAmals(normalized);
          const nextMap = Object.fromEntries((normalized || []).map(a => [a.id, a.count]));
          previousAmalCountsRef.current = nextMap;
        } catch (e) {
          localStorage.removeItem('amals');
        }
      }
    }
  };

  const fetchQuranData = async () => {
    try {
      console.log('Fetching Quran data...');
      
      // Fetch current completion (use .limit(1) instead of .single() to handle multiple rows)
      const { data: currentData, error: currentError } = await supabase
        .from('quran_completions')
        .select('*')
        .eq('status', 'current')
        .limit(1);

      console.log('Current completion data:', currentData);
      console.log('Current completion error:', currentError);

      if (currentError) {
        throw new Error(currentError.message);
      }

      // Fetch completed completions
      const { data: completedData, error: completedError } = await supabase
        .from('quran_completions')
        .select('*')
        .eq('status', 'completed')
        .order('completion_number', { ascending: false })
        .limit(5); // Show only last 5 completions

      console.log('Completed completions data:', completedData);
      console.log('Completed completions error:', completedError);

      if (completedError) {
        throw new Error(completedError.message);
      }

      // Set fallback data only if there's a real error, not just "no data found"
      const current = currentData && currentData.length > 0 ? currentData[0] : null;
      if (!current) {
        console.log('No current completion found, creating fallback data');
        setCurrentCompletion({
          id: 1,
          status: 'current',
          completion_number: 1,
          completed_parts: [1, 2, 3, 5, 8, 12, 15, 18, 22, 25, 28]
        });
      } else {
        console.log('Setting current completion from database');
        setCurrentCompletion(current);
        // Clear optimistic update when we get real data
        setOptimisticCompletion(null);
      }

      setCompletedCompletions(completedData || []);
      console.log('Quran data fetch completed successfully');
    } catch (err) {
      console.error('Quran data fetch error:', err);
      // Set fallback data only on real errors
      setCurrentCompletion({
        id: 1,
        status: 'current',
        completion_number: 1,
        completed_parts: [1, 2, 3, 5, 8, 12, 15, 18, 22, 25, 28]
      });
      setCompletedCompletions([
        {
          id: 2,
          status: 'completed',
          completion_number: 1,
          completed_parts: Array.from({ length: 30 }, (_, i) => i + 1),
          completed_at: new Date().toISOString()
        }
      ]);
    }
  };

  // Listen for real-time updates from Supabase - Quran changes
  useEffect(() => {
    const channel = supabase
      .channel('quran_completions')
      .on('postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'quran_completions', filter: 'status=eq.current' },
        (payload) => {
          console.log('Real-time update received:', payload);
          if (payload.new) {
            // Only update if the data is actually different
            const currentParts = new Set(currentCompletion?.completed_parts || []);
            const newParts = new Set(payload.new.completed_parts || []);
            
            // Check if there are actual changes
            const hasChanges = 
              currentParts.size !== newParts.size ||
              Array.from(currentParts).some(part => !newParts.has(part)) ||
              Array.from(newParts).some(part => !currentParts.has(part));
            
            if (hasChanges) {
              console.log('Updating with real-time data');
              setCurrentCompletion(payload.new);
              setOptimisticCompletion(null); // Clear optimistic state
              
              // Trigger animations for changes
              const newlyCompleted = Array.from(newParts).filter(part => !currentParts.has(part));
              const newlyUncompleted = Array.from(currentParts).filter(part => !newParts.has(part));
              
              // Animate changes
              newlyCompleted.forEach(partNumber => {
                animateQuranPart(partNumber, true);
              });
              
              newlyUncompleted.forEach(partNumber => {
                animateQuranPart(partNumber, false);
              });
              
              setPreviousCompletedParts(newParts);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Listen for real-time updates from Supabase - Dhikr changes
  useEffect(() => {
    const channel = supabase
      .channel('dhikrs')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'dhikrs' },
        (payload) => {
          console.log('Dhikr real-time update received:', payload);
          // Refresh dhikr data when any dhikr changes
          fetchDhikrs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchDhikrs = async () => {
    try {
      const { data, error } = await supabase
        .from('dhikrs')
        .select('*')
        .order('order_num');

      if (error) {
        throw new Error(error.message);
      }
      
      setDhikrs(data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
      // Fallback data
      setDhikrs([
        { id: 1, name: "سبحان الله", count: 48066, order_num: 1 },
        { id: 2, name: "الحمدلله", count: 35240, order_num: 2 },
        { id: 3, name: "لا إله إلا الله", count: 28950, order_num: 3 },
        { id: 4, name: "الله أكبر", count: 18750, order_num: 4 },
        { id: 5, name: "أستغفر الله", count: 12300, order_num: 5 }
      ]);
    }
  };

  const animateCount = (dhikrId, targetCount) => {
    let current = 0;
    const duration = 1000;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      current = Math.floor(targetCount * progress);

      setAnimatedCounts(prev => ({
        ...prev,
        [dhikrId]: current
      }));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setTimeout(() => {
          setAnimatedCounts(prev => {
            const newCounts = { ...prev };
            delete newCounts[dhikrId];
            return newCounts;
          });
        }, 500);
      }
    };

    requestAnimationFrame(animate);
  };

  useEffect(() => {
    if (dhikrs.length > 0) {
      dhikrs.forEach(dhikr => {
        const previousCount = previousCounts[dhikr.id] || 0;
        if (previousCount !== dhikr.count) {
          animateCount(dhikr.id, dhikr.count);
        }
      });

      const newPreviousCounts = {};
      dhikrs.forEach(dhikr => {
        newPreviousCounts[dhikr.id] = dhikr.count;
      });
      setPreviousCounts(newPreviousCounts);
    }
  }, [dhikrs]);

  // Animate Quran parts changes
  useEffect(() => {
    if (currentCompletion && currentCompletion.completed_parts) {
      const currentPartsSet = new Set(currentCompletion.completed_parts);
      const previousPartsSet = previousCompletedParts;
      
      // Find newly completed parts
      const newlyCompleted = Array.from(currentPartsSet).filter(part => !previousPartsSet.has(part));
      // Find newly uncompleted parts
      const newlyUncompleted = Array.from(previousPartsSet).filter(part => !currentPartsSet.has(part));
      
      // Animate newly completed parts
      newlyCompleted.forEach(partNumber => {
        animateQuranPart(partNumber, true);
      });
      
      // Animate newly uncompleted parts
      newlyUncompleted.forEach(partNumber => {
        animateQuranPart(partNumber, false);
      });
      
      setPreviousCompletedParts(currentPartsSet);
    }
  }, [currentCompletion]);

  const animateQuranPart = (partNumber, isCompleted) => {
    const element = document.getElementById(`quran-part-${partNumber}`);
    if (!element) return;
    
    // Add animation class
    element.style.transition = 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
    
    if (isCompleted) {
      // Completion animation
      element.style.transform = 'scale(1.2)';
      element.style.boxShadow = '0 8px 25px rgba(0, 191, 255, 0.6)';
      element.style.background = 'linear-gradient(135deg, #00BFFF, #0099CC)';
      element.style.borderColor = '#00BFFF';
      element.style.color = 'white';
      
      setTimeout(() => {
        element.style.transform = 'scale(1)';
        element.style.boxShadow = '0 2px 8px rgba(0, 191, 255, 0.4)';
      }, 300);
    } else {
      // Uncompletion animation
      element.style.transform = 'scale(0.8)';
      element.style.opacity = '0.5';
      
      setTimeout(() => {
        element.style.background = 'rgba(255, 255, 255, 0.1)';
        element.style.borderColor = 'rgba(255, 255, 255, 0.3)';
        element.style.color = 'rgba(255, 255, 255, 0.9)';
        element.style.transform = 'scale(1)';
        element.style.opacity = '1';
        element.style.boxShadow = 'none';
      }, 200);
    }
  };

  const resetDhikr = async (id) => {
    try {
      const dhikr = dhikrs.find(d => d.id === id);
      if (dhikr) {
        animateCount(id, dhikr.count);
      }
    } catch (err) {
      console.error('Reset error:', err);
    }
  };

  const resetAmal = async (id) => {
    try {
      const amal = amals.find(a => a.id === id);
      if (amal) {
        animateAmalCount(id, amal.count);
      }
    } catch (err) {
      console.error('Amal reset error:', err);
    }
  };

  const animateAmalCount = (id, targetCount) => {
    const safeTarget = Math.max(0, parseInt(targetCount) || 0);

    setAnimatedAmalCounts(prev => ({ ...prev, [id]: 0 }));
    
    let currentCount = 0;
    const increment = safeTarget / 30;
    
    const timer = setInterval(() => {
      currentCount += increment;
      if (currentCount >= safeTarget) {
        currentCount = safeTarget;
        clearInterval(timer);
      }
      setAnimatedAmalCounts(prev => ({ ...prev, [id]: Math.floor(currentCount) }));
    }, 30);
  };

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      backgroundColor: '#000000',
      backgroundImage: 'radial-gradient(circle at 25% 35%, rgba(0, 30, 60, 0.9) 0%, rgba(0, 20, 40, 0.7) 25%, rgba(0, 15, 30, 0.5) 50%, rgba(0, 10, 20, 0.3) 75%, rgba(0, 0, 0, 0.1) 100%), linear-gradient(135deg, rgba(0, 40, 80, 0.4) 0%, rgba(0, 30, 60, 0.3) 50%, rgba(0, 20, 40, 0.2) 100%), radial-gradient(circle at 75% 65%, rgba(0, 50, 100, 0.5) 0%, rgba(0, 35, 70, 0.4) 30%, rgba(0, 25, 50, 0.3) 60%, rgba(0, 0, 0, 0.1) 100%)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      boxSizing: 'border-box',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        padding: window.innerWidth < 768 ? '10px' : '20px',
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{
          marginBottom: '15px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <img 
            src="/logo.png" 
            alt="مسجد جامع"
            style={{
              width: window.innerWidth < 768 ? '80px' : '150px',
              height: window.innerWidth < 768 ? '80px' : '150px',
              objectFit: 'contain',
              filter: 'brightness(0) invert(1) drop-shadow(0 0 30px rgba(255, 255, 255, 0.8))',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.filter = 'brightness(0) invert(1) drop-shadow(0 0 40px rgba(255, 255, 255, 1))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.filter = 'brightness(0) invert(1) drop-shadow(0 0 30px rgba(255, 255, 255, 0.8))';
            }}
          />
        </div>
      </div>

      {/* Main Content - Three Sections */}
      <div style={{
        display: window.innerWidth < 768 ? 'flex' : 'flex',
        flexDirection: window.innerWidth < 768 ? 'column' : 'row',
        gap: window.innerWidth < 768 ? '10px' : '20px',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden'
      }}>
        {/* Left Section - Dhikr Counter */}
        <div style={{
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
          transform: 'translateX(0)',
          opacity: 1,
          transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '15px',
            padding: window.innerWidth < 768 ? '10px' : '15px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <h2 style={{
              fontSize: window.innerWidth < 768 ? '16px' : '20px',
              color: 'white',
              margin: '0 0 15px 0',
              textAlign: 'center',
              fontFamily: 'AbarMidFaNum, Arial, sans-serif',
              fontWeight: 'bold',
              flexShrink: 0
            }}>
              اذکار
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth < 768 ? '1fr 1fr' : '1fr 1fr',
              gap: window.innerWidth < 768 ? '8px' : '12px',
              flex: 1,
              overflow: 'hidden',
              alignContent: 'stretch'
            }}>
              {dhikrs.slice(0, 4).map(dhikr => (
                <div
                  key={dhikr.id}
                  onClick={() => resetDhikr(dhikr.id)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '12px',
                    padding: window.innerWidth < 768 ? '12px' : '20px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100%',
                    height: '100%',
                    transform: 'translateY(0)',
                    opacity: 1,
                    boxSizing: 'border-box'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 255, 255, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    fontSize: window.innerWidth < 768 
                      ? (dhikr.name.length > 20 ? '14px' : '16px')
                      : (dhikr.name.length > 20 ? '20px' : '24px'),
                    color: '#ffffff',
                    fontFamily: 'Al-QuranAlKareem, AbarMidFaNum, Arial, sans-serif',
                    lineHeight: '1.3',
                    direction: 'rtl',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    marginBottom: '12px',
                    alignSelf: 'center',
                    width: '100%',
                    padding: '0 5px',
                    boxSizing: 'border-box'
                  }}>
                    {dhikr.name}
                  </div>
                  
                  <div style={{
                    fontSize: window.innerWidth < 768 ? '24px' : '32px',
                    color: '#00BFFF',
                    fontFamily: 'AbarMidFaNum, monospace',
                    textShadow: '0 0 15px rgba(0, 191, 255, 0.9)',
                    fontWeight: 'bold',
                    alignSelf: 'center'
                  }}>
                    {animatedCounts[dhikr.id] !== undefined
                      ? animatedCounts[dhikr.id].toLocaleString('fa-IR')
                      : dhikr.count.toLocaleString('fa-IR')
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Middle Section - Amals */}
        <div style={{
          flex: '0.6',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
          transform: 'translateX(0)',
          opacity: 1,
          transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '15px',
            padding: window.innerWidth < 768 ? '10px' : '15px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <h2 style={{
              fontSize: window.innerWidth < 768 ? '16px' : '20px',
              color: 'white',
              margin: '0 0 15px 0',
              textAlign: 'center',
              fontFamily: 'AbarMidFaNum, Arial, sans-serif',
              fontWeight: 'bold',
              flexShrink: 0
            }}>
              اعمال
            </h2>
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: window.innerWidth < 768 ? '8px' : '12px',
              flex: 1,
              overflow: 'hidden'
            }}>
              {amals.map(amal => (
                <div
                  key={amal.id}
                  onClick={() => resetAmal(amal.id)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '12px',
                    padding: window.innerWidth < 768 ? '12px' : '15px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    minHeight: window.innerWidth < 768 ? '60px' : '70px',
                    transform: 'translateY(0)',
                    opacity: 1,
                    boxSizing: 'border-box'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 255, 255, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    fontSize: window.innerWidth < 768 ? '14px' : '16px',
                    color: '#ffffff',
                    fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                    lineHeight: '1.3',
                    direction: 'rtl',
                    textAlign: 'right',
                    fontWeight: 'bold',
                    flex: '1'
                  }}>
                    {amal.name}
                  </div>
                  
                  <div style={{
                    fontSize: window.innerWidth < 768 ? '20px' : '24px',
                    color: '#00BFFF',
                    fontFamily: 'AbarMidFaNum, monospace',
                    textShadow: '0 0 15px rgba(0, 191, 255, 0.9)',
                    fontWeight: 'bold',
                    minWidth: window.innerWidth < 768 ? '40px' : '50px',
                    textAlign: 'center'
                  }}>
                    {animatedAmalCounts[amal.id] !== undefined
                      ? animatedAmalCounts[amal.id].toLocaleString('fa-IR')
                      : (Number.isFinite(parseInt(amal.count)) ? parseInt(amal.count) : 0).toLocaleString('fa-IR')
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Section - Quran Completion */}
        <div style={{
          flex: window.innerWidth < 768 ? '1' : '1.2',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: window.innerWidth < 768 ? '15px' : '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: '0'
          }}>
            {/* Header with completion number */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px',
              position: 'relative'
            }}>
              <div style={{
                fontSize: window.innerWidth < 768 ? '28px' : '42px',
                color: '#00BFFF',
                fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                fontWeight: 'bold',
                letterSpacing: '-2px',
                lineHeight: '1',
                flex: '0 0 auto'
              }}>
                {currentCompletion?.completion_number || 1}
              </div>
              
              {/* Center title - absolutely positioned */}
              <div style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: window.innerWidth < 768 ? '16px' : '20px',
                color: 'rgba(255, 255, 255, 0.9)',
                fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}>
                ختم قرآن
              </div>
              
              <div style={{
                fontSize: window.innerWidth < 768 ? '24px' : '38px',
                color: '#00BFFF',
                fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                fontWeight: 'bold',
                letterSpacing: '-2px',
                lineHeight: '1',
                flex: '0 0 auto'
              }}>
                {(currentCompletion?.completed_parts || []).length}/30
              </div>
            </div>

            {/* Small labels */}
            <div style={{
              display: window.innerWidth < 768 ? 'none' : 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px',
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.6)',
              fontFamily: 'AbarMidFaNum, Arial, sans-serif',
              letterSpacing: '0.5px'
            }}>
              <div>شماره ختم</div>
              <div>جزء تکمیل شده </div>
            </div>

            {/* 30 Parts Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth < 768 ? 'repeat(5, 1fr)' : 'repeat(6, 1fr)',
              gridTemplateRows: window.innerWidth < 768 ? 'repeat(6, 1fr)' : 'repeat(5, 1fr)',
              gap: window.innerWidth < 768 ? '2px' : '3px',
              flex: 1,
              direction: 'rtl',
              overflow: 'hidden',
              minHeight: '0'
            }}>
              {Array.from({ length: 30 }, (_, i) => i + 1).map(partNumber => {
                // Use optimistic completion if available, otherwise use current completion
                const completionToShow = optimisticCompletion || currentCompletion;
                const completedParts = completionToShow?.completed_parts || [];
                const isCompleted = completedParts.includes(partNumber);
                const isOptimistic = optimisticCompletion && optimisticCompletion.completed_parts?.includes(partNumber);
                
                return (
                  <div
                    key={partNumber}
                    id={`quran-part-${partNumber}`}
                    style={{
                      borderRadius: '4px',
                      background: isCompleted ? 
                        'linear-gradient(135deg, #00BFFF, #0099CC)' : 
                        'rgba(255, 255, 255, 0.1)',
                      border: `2px solid ${isCompleted ? '#00BFFF' : 'rgba(255, 255, 255, 0.3)'}`,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: window.innerWidth < 768 ? '10px' : '14px',
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                      fontWeight: 'bold',
                      color: isCompleted ? 'white' : 'rgba(255, 255, 255, 0.9)',
                      textShadow: isCompleted ? '0 1px 3px rgba(0, 0, 0, 0.8)' : 'none',
                      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: isCompleted ? '0 2px 8px rgba(0, 191, 255, 0.4)' : '0 1px 4px rgba(0, 0, 0, 0.3)',
                      direction: 'rtl',
                      minHeight: '0',
                      overflow: 'hidden',
                      padding: window.innerWidth < 768 ? '2px' : '4px',
                      textAlign: 'center',
                      lineHeight: '1.1',
                      transform: 'scale(1)',
                      position: 'relative',
                      opacity: isOptimistic ? 0.8 : 1
                    }}
                  >
                    {/* Optimistic update indicator */}
                    {isOptimistic && (
                      <div style={{
                        position: 'absolute',
                        top: '1px',
                        right: '1px',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#FFA500',
                        animation: 'pulse 1s infinite'
                      }} />
                    )}
                    جزء {partNumber}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {error && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(255, 0, 0, 0.1)',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          borderRadius: '8px',
          padding: '10px 15px',
          color: '#ff6b6b',
          fontSize: '12px',
          fontFamily: 'AbarMidFaNum, Arial, sans-serif',
          textAlign: 'center',
          zIndex: 1000
        }}>
          خطا در اتصال به دیتابیس: {error}
        </div>
      )}
    </div>
  );
}

export default App;